import classNames from 'classnames';
import React, { useState } from 'react';
import Button from '../components/Button';
import Caret from '../components/Caret';
import FloatingCard from '../components/FloatingCard';
import Popover from '../components/Popover';
import { TopicPostsOrder, TopicPostsType } from '../graphql/generated/graphql';
import allPostsIcon from '../icons/non-prefilled/16x16/allPosts.svg';
import alphabeticalIcon from '../icons/non-prefilled/16x16/alphabetical.svg';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import popularityIcon from '../icons/non-prefilled/16x16/popularity.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import tocIcon from '../icons/non-prefilled/16x16/tableOfContents.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import viewedIcon from '../icons/non-prefilled/16x16/viewed.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import * as styles from './TopicListDefaultsSettings.module.scss';
import type { EditTopicState } from './TopicView';

interface TopicListDefaultsSettingsProps {
  editTopicState: EditTopicState & { touched: boolean };
  updateEditTopicState: (payload: Partial<EditTopicState | null>) => void;
  postCounts: Record<TopicPostsType, number>;
}

const TopicListDefaultsSettings = ({
  editTopicState,
  updateEditTopicState,
  postCounts,
}: TopicListDefaultsSettingsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState<TopicPostsType>(
    editTopicState.defaultShow ?? TopicPostsType.Published,
  );
  const [sort, setSort] = useState<TopicPostsOrder>(
    editTopicState.defaultSort ?? TopicPostsOrder.Popularity,
  );
  const isChanged =
    editTopicState.defaultShow !== show || editTopicState.defaultSort !== sort;

  const resetDefaults = () => {
    setSort(editTopicState.defaultSort ?? TopicPostsOrder.Popularity);
    setShow(editTopicState.defaultShow ?? TopicPostsType.Published);
  };

  return (
    <div className={styles.container}>
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          resetDefaults();
          setIsOpen(open);
        }}
        view={
          <FloatingCard noArrow>
            <div className={styles.menu}>
              <div className={styles.menuRow}>
                Sort by
                <PostFilterMenu
                  onChange={(value) => setSort(value)}
                  options={{
                    [TopicPostsOrder.Popularity]: {
                      icon: popularityIcon,
                      label: 'Most Popular',
                    },
                    [TopicPostsOrder.PublishedAt]: {
                      icon: publishIcon,
                      label: 'Publish Date',
                    },
                    [TopicPostsOrder.UpdatedAt]: {
                      icon: editIcon,
                      label: 'Last Edited',
                    },
                    [TopicPostsOrder.ViewedAt]: {
                      icon: viewedIcon,
                      label: 'Recent View',
                    },
                    [TopicPostsOrder.Title]: {
                      icon: alphabeticalIcon,
                      label: 'Alphabetical',
                    },
                  }}
                  menuPlacement="bottom-end"
                  value={sort}
                  buttonProps={{
                    className: styles.menuButton,
                    variant: 'secondary',
                    size: 'small',
                  }}
                />
              </div>
              <div className={styles.menuRow}>
                <span>Show</span>
                <PostFilterMenu<TopicPostsType>
                  onChange={(value) => setShow(value)}
                  options={{
                    [TopicPostsType.Published]: {
                      icon: postIcon,
                      label: 'Published',
                      count: postCounts.PUBLISHED,
                    },
                    [TopicPostsType.Verified]: {
                      icon: verifiedIcon,
                      label: 'Verified',
                      count: postCounts.VERIFIED,
                    },
                    [TopicPostsType.Template]: {
                      icon: templateIcon,
                      label: 'Template',
                      count: postCounts.TEMPLATE,
                    },
                    [TopicPostsType.Archived]: {
                      icon: archiveIcon,
                      label: 'Archived',
                      count: postCounts.ARCHIVED,
                    },
                    [TopicPostsType.Draft]: {
                      icon: draftsIcon,
                      label: 'Drafts',
                      count: postCounts.DRAFT,
                    },
                    [TopicPostsType.All]: {
                      icon: allPostsIcon,
                      label: 'All posts',
                      count: postCounts.ALL,
                    },
                  }}
                  showCountInLabel
                  value={show}
                  buttonProps={{
                    className: styles.menuButton,
                    variant: 'secondary',
                    size: 'small',
                  }}
                />
              </div>
            </div>
            {isChanged && (
              <div className={styles.defaultsButton}>
                <Button
                  variant="muted"
                  size="small"
                  onClick={() => {
                    resetDefaults();
                    setIsOpen(false);
                  }}
                >
                  Reset defaults
                </Button>
                <Button
                  variant="link"
                  size="small"
                  onClick={() => {
                    updateEditTopicState({
                      defaultShow: show,
                      defaultSort: sort,
                    });
                    setIsOpen(false);
                  }}
                >
                  Set as defaults
                </Button>
              </div>
            )}
          </FloatingCard>
        }
        placement="bottom-end"
      >
        <Button
          variant="secondary"
          size="small"
          icon={tocIcon}
          className={classNames(styles.openButton, { [styles.open]: isOpen })}
        >
          List defaults
          <span className={styles.caret}>
            <Caret direction={isOpen ? 'up' : 'down'} />
          </span>
        </Button>
      </Popover>
    </div>
  );
};

export default TopicListDefaultsSettings;
